import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";

interface Option {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string | number;
  onChange: (value: string, option?: Option) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  freeText?: boolean;
  inputText?: string; // New prop for free text display
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "انتخاب کنید...",
  label,
  className,
  required = false,
  disabled = false,
  freeText = false,
  inputText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const updatePosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Sync internal search term with value in freeText mode
  useEffect(() => {
    if (freeText) {
      // Only if value matches an option explicitly, we show the label
      // Otherwise we trust inputText or fallback to value string
      const selectedOption = options.find(
        (o) => String(o.value) === String(value),
      );

      // If we have a valid selected option (ID match), use its label
      if (selectedOption && String(value) !== "") {
        setSearchTerm(selectedOption.label);
      } else {
        // Otherwise use the input text provided by parent (what user typed)
        // Fallback to value if inputText is undefined (backward compatibility)
        setSearchTerm(
          inputText !== undefined ? inputText : String(value || ""),
        );
      }
    }
  }, [value, freeText, options, inputText]);

  // Handle scroll/resize
  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (!isOpen) return;
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(event.target as Node)
      ) {
        return;
      }
      requestAnimationFrame(updatePosition);
    };

    const handleResize = () => {
      if (isOpen) requestAnimationFrame(updatePosition);
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updatePosition]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focus search input only in standard mode
  useEffect(() => {
    if (isOpen && searchInputRef.current && !freeText) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen, freeText]);

  // In freeText mode, filtering is done against the main input value (searchTerm)
  // In standard mode, filtering is done against the internal searchTerm state from the dropdown input
  const filterQuery = freeText ? searchTerm : searchTerm;

  const filteredOptions = options.filter((option) =>
    (option.label || "")
      .toLowerCase()
      .includes((filterQuery || "").toLowerCase()),
  );

  const selectedOption = options.find((o) => String(o.value) === String(value));

  const handleSelect = (optionValue: string | number) => {
    const opt = options.find((o) => String(o.value) === String(optionValue));
    onChange(String(optionValue), opt); // Pass the full option object
    setIsOpen(false);
    if (!freeText) {
      setSearchTerm("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  const handleFreeTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal, undefined); // Pass undefined as option to indicate typing
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className={`w-full ${className || ""}`} ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Trigger Button or Input */}
        {freeText ? (
          <div className="relative">
            <input
              type="text"
              className={`
                        w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border 
                        ${isOpen ? "border-blue-500 ring-2 ring-blue-500/20" : "border-slate-200 dark:border-slate-700"} 
                        ${disabled ? "opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800" : ""}
                        rounded-xl outline-none transition-all text-slate-800 dark:text-white placeholder:text-slate-400
                    `}
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleFreeTextChange}
              onFocus={() => !disabled && setIsOpen(true)}
              disabled={disabled}
            />
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {searchTerm && !disabled && (
                <button
                  onClick={handleClear}
                  className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-400 hover:text-red-500 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
              <ChevronDown
                size={16}
                className="text-slate-400 pointer-events-none"
              />
            </div>
          </div>
        ) : (
          <div
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={`
                w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border 
                ${isOpen ? "border-blue-500 ring-2 ring-blue-500/20" : "border-slate-200 dark:border-slate-700"} 
                ${disabled ? "opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800" : "cursor-pointer"}
                rounded-xl flex items-center justify-between transition-all
                text-slate-800 dark:text-white
            `}
          >
            <span
              className={`truncate ${!selectedOption ? "text-slate-400" : ""}`}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>

            <div className="flex items-center gap-2 shrink-0">
              {selectedOption && !required && !disabled && (
                <button
                  onClick={handleClear}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-400 hover:text-red-500 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
              <ChevronDown
                size={18}
                className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </div>
          </div>
        )}

        {/* Portal Dropdown Menu */}
        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 flex flex-col"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: "300px",
              }}
            >
              {/* Search Input (Only for standard mode) */}
              {!freeText && (
                <div className="p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
                  <div className="relative">
                    <Search
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      size={16}
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="w-full bg-white dark:bg-slate-950 text-sm p-2 pr-9 rounded-lg outline-none text-slate-800 dark:text-white placeholder:text-slate-400 border border-slate-200 dark:border-slate-700 focus:border-blue-500"
                      placeholder="جستجو..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Options List */}
              <div className="flex-1 overflow-y-auto p-1 custom-scrollbar">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(option.value);
                      }}
                      className={`
                      px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors
                      ${
                        String(value) === String(option.value)
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }
                    `}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-8 text-center text-sm text-slate-400">
                    {freeText
                      ? "گزینهای یافت نشد (مقدار جدید ثبت میشود)"
                      : "موردی یافت نشد."}
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};
